'use client';

import { useEffect, useLayoutEffect, useState } from 'react';
import Link from 'next/link';
import { E, editorialBtnInlineBase, editorialErrorActionsRow } from '@/lib/editorial';
import { reportClientBoundaryError } from '@/lib/report-client-boundary-error';

const RELOAD_TS_KEY = 'nla_error_autoreload_ts';
/**
 * If we hit the error boundary again within this many ms of scheduling a reload, show the error UI
 * (reload did not fix it).
 *
 * **Edge cases** (see DEPLOYMENT_ERROR_HANDLING.md):
 * - **Private / strict storage**: `sessionStorage` throws → catch shows error UI immediately (no reload loop).
 * - **Slow devices**: reload is delayed only by the timeout; if hydration races, user may briefly see “Refreshing…” then the full error state.
 * - **Non-recoverable errors**: first visit schedules reload; if the same bug fires again within the window,
 *   user may see short “Refreshing…” flashes until they leave or the window exceeds the post-reload window.
 */
const POST_RELOAD_WINDOW_MS = 8000;

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const [phase, setPhase] = useState<'recovering' | 'error'>(() =>
    process.env.NODE_ENV === 'development' ? 'error' : 'recovering'
  );

  useEffect(() => {
    console.error('Global error boundary caught an error:', error);

    if (process.env.NODE_ENV === 'production') {
      console.error('Production error:', {
        message: error.message,
        stack: error.stack,
        digest: error.digest,
        timestamp: new Date().toISOString(),
      });
      reportClientBoundaryError(error, 'segment');
    }
  }, [error]);

  useLayoutEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      setPhase('error');
      return;
    }

    let timeoutId: number | undefined;

    try {
      const raw = sessionStorage.getItem(RELOAD_TS_KEY);
      const ts = raw ? parseInt(raw, 10) : 0;
      const withinRetryWindow = ts > 0 && Date.now() - ts < POST_RELOAD_WINDOW_MS;

      if (withinRetryWindow) {
        sessionStorage.removeItem(RELOAD_TS_KEY);
        setPhase('error');
        return;
      }

      sessionStorage.setItem(RELOAD_TS_KEY, String(Date.now()));
      setPhase('recovering');
      timeoutId = window.setTimeout(() => {
        window.location.reload();
      }, 450);
    } catch {
      setPhase('error');
    }

    return () => {
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  if (phase === 'recovering') {
    return (
      <div style={{ ...E.page, padding: 0 }}>
        <div className="e-pad" style={{ maxWidth: 520, margin: '0 auto', paddingTop: 80, paddingBottom: 80 }}>
          <div style={E.label}>Error · Application</div>
          <div aria-live="polite" aria-atomic="true">
            <p style={{ ...E.body, color: E.muted, marginTop: 8 }}>
              Refreshing the page…
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...E.page, padding: 0 }}>
      <div className="e-pad" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 56, paddingBottom: 80 }}>
        <div style={E.label}>Error · Application</div>

        <p
          style={{
            fontFamily: E.serif,
            fontSize: 'clamp(72px, 18vw, 120px)',
            fontWeight: 400,
            fontStyle: 'italic',
            lineHeight: 0.9,
            margin: '0 0 8px',
            color: E.goldDeep,
            opacity: 0.45,
            letterSpacing: '-0.02em',
          }}
        >
          !
        </p>

        <h1 role="alert" style={{ ...E.h1, fontSize: 'clamp(36px, 5vw, 52px)', marginBottom: 16 }}>
          Something went wrong.
        </h1>

        <p style={{ ...E.body, color: E.muted, maxWidth: 520, marginBottom: error.digest ? 10 : 28 }}>
          A problem occurred while loading this page. Try again, or return home and come back in a moment.
        </p>

        {error.digest ? (
          <p
            style={{
              ...E.cap,
              color: E.muted,
              opacity: 0.85,
              marginBottom: 28,
            }}
          >
            Reference: {error.digest}
          </p>
        ) : null}

        {process.env.NODE_ENV === 'development' && (
          <details
            style={{
              ...E.body,
              fontSize: 13,
              color: E.inkSoft,
              marginBottom: 28,
              maxWidth: 520,
            }}
          >
            <summary style={{ cursor: 'pointer', color: E.goldDeep, ...E.cap, fontSize: 10 }}>
              Technical details (development only)
            </summary>
            <pre
              style={{
                marginTop: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                padding: 16,
                background: E.cream2,
                border: `1px solid ${E.ruleSoft}`,
                fontSize: 11,
                lineHeight: 1.5,
                fontFamily: 'ui-monospace, monospace',
                color: E.ink,
              }}
            >
              {error.message}
              {error.stack ? `\n\n${error.stack}` : ''}
            </pre>
          </details>
        )}

        <div style={editorialErrorActionsRow}>
          <button type="button" onClick={reset} style={{ ...E.btn, ...editorialBtnInlineBase }}>
            Try again
          </button>
          <Link href="/" style={{ ...E.btnGhost, ...editorialBtnInlineBase }}>
            Return home
          </Link>
        </div>
      </div>
    </div>
  );
}
